import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { FormElement, Form, Field, FormHandle } from '@progress/kendo-react-form'
import { GridRowClickEvent, GridRowProps } from '@progress/kendo-react-grid'
import DefaultButton from 'components/Buttons/DefaultButton/DefaultButton'
import FormDateSpanSelector, { IFormDateSpanSelectorRef } from 'components/Form/FormDateSpanSelector'
import FormSwitch from 'components/Form/FormSwitch'
import FormTextArea from 'components/Form/FormTextArea'
import SAGrid, { GetDataResponse, GridGetDataProps, ISAGridRef } from 'components/Grid/SAGrid'
import ConfirmModal from 'components/Modals/ConfirmModal'
import { DEFAULT_PERMISSION_MESSAGE } from 'core/consts'
import { V1ApiCasesClient, V1ApiSharedClient } from 'core/http-common'
import toast from 'core/toast'
import { CreateNoteDto, NoteDto, NoteTypeEnum, UpdateNoteDto } from 'generated/v1/v1SharedApi'
import moment from 'moment'
import React, { ReactNode, useEffect } from 'react'
import { useRef, useState } from 'react'
import { Col, Row } from 'react-bootstrap'
import { useDispatch } from 'react-redux'
import { endLoadingAction, startLoadingAction } from 'state/actions/appActions'
import { hasPermission } from 'state/reduxHelpers'
import { AppDispatch } from 'state/store'
import { DcsPermissions, GridCellEnum, ICustomColumn, IDateSearchLocalStorageNames, IGridLocalStorageNames } from 'types'
import './NotesForm.css'
import { RequiredValidator } from 'core/Validators'

interface IProps {
    noteTypeId?: number
    noteType: NoteTypeEnum
    editable: boolean
    formIsModified: boolean
    hideDateOptions?: boolean | undefined 
}

const LOCAL_STORAGE_KEYS = {
    CASES: {
        RELATIVE_DATE_OPTION: 'caseNotesRelativeDateOption',
        END_DATE: 'caseNotesEndDate',
        START_DATE: 'caseNotesStartDate',
        SORT: 'caseNotesSort',
        SEARCH: 'caseNotesSearch',
        PAGE_SIZE: 'caseNotesPageSize',
        SELECTED_PAGE: 'caseNotesSelectedPage'
    },
    DEALERS: {
        RELATIVE_DATE_OPTION: 'dealerNotesRelativeDateOption',
        END_DATE: 'dealerNotesEndDate',
        START_DATE: 'dealerNotesStartDate',
        SORT: 'dealerNotesSort',
        SEARCH: 'dealerNotesSearch',
        PAGE_SIZE: 'dealerNotesPageSize',
        SELECTED_PAGE: 'dealerNotesSelectedPage'
    }
}

const NotesForm = ({ editable, formIsModified, noteTypeId, noteType, hideDateOptions }: IProps) => {

    const [showAddModal, setShowAddModal] = useState(false)
    const [showEditModal, setShowEditModal] = useState(false)

    const formAddModal = useRef<FormHandle>(null)
    const [addNoteModalErrors, setAddNoteModalErrors] = useState<Array<string>>([])
    const formEditModal = useRef<FormHandle>(null)
    const [editNoteModalErrors, setEditNoteModalErrors] = useState<Array<string>>([])
    const [refreshGrid, setRefreshGrid] = useState<number>(0)
    const [disableSubmit, setDisableSubmit] = useState(false)

    const maxDate = moment().endOf('day').toDate()
    const dispatch = useDispatch<AppDispatch>()

    let dateSearchLocalStorageNames: IDateSearchLocalStorageNames = {
        startDate: noteType && noteTypeId && noteType == NoteTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.START_DATE : LOCAL_STORAGE_KEYS.CASES.START_DATE,
        endDate: noteType && noteTypeId && noteType == NoteTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.END_DATE : LOCAL_STORAGE_KEYS.CASES.END_DATE,
        relativeDateOption: noteType && noteTypeId && noteType == NoteTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.RELATIVE_DATE_OPTION : LOCAL_STORAGE_KEYS.CASES.RELATIVE_DATE_OPTION,
    }
    let gridLocalStorageNames: IGridLocalStorageNames = {
        sort: noteType && noteTypeId && noteType == NoteTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.SORT : LOCAL_STORAGE_KEYS.CASES.SORT,
        search: noteType && noteTypeId && noteType == NoteTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.SEARCH : LOCAL_STORAGE_KEYS.CASES.SEARCH,
        selectedPage: noteType && noteTypeId && noteType == NoteTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.SELECTED_PAGE : LOCAL_STORAGE_KEYS.CASES.SELECTED_PAGE,
        pageSize: noteType && noteTypeId && noteType == NoteTypeEnum.Dealer ? LOCAL_STORAGE_KEYS.DEALERS.PAGE_SIZE : LOCAL_STORAGE_KEYS.CASES.PAGE_SIZE,
    }

    const gridRef = useRef<ISAGridRef>(null)
    const formDateSpanSelectorRef = useRef<IFormDateSpanSelectorRef>(null)
    const formRef = useRef<FormHandle>(null)

    const searchByDate = async (start: Date, end: Date) => {
        formRef.current?.valueSetter('startDate', start)
        formRef.current?.valueSetter('endDate', end)
        gridRef.current?.resetAndFetch()
    }

    const [editNote, setEditNote] = useState<NoteDto>()

    const getData = async (props: GridGetDataProps): Promise<GetDataResponse> => {

        if (noteTypeId && noteType && gridLocalStorageNames) {

            let values = formRef.current?.values
            let page = props.paging && props.paging.currentPage ? props.paging.currentPage : 1

            let defaultStart = moment('01-01-2001', 'MM-DD-YYYY').toDate()
            let defaultEnd = moment().endOf('day').toDate()

            try {
                const result = await V1ApiSharedClient.getNotes(
                    noteTypeId,
                    noteType,
                    localStorage.getItem(gridLocalStorageNames.search) ?? '',
                    props.sortBy,
                    props.sortAscending,
                    page,
                    props.paging ? props.paging.take : 25,
                    true,
                    !hideDateOptions ? ( values?.startDate ? values.startDate : undefined) : defaultStart,
                    !hideDateOptions ? ( values?.endDate ? values.endDate : undefined ) : defaultEnd,
                )

                return {
                    data: result.data,
                    total: result.totalItems ? result.totalItems : 0
                }

            } catch (e) {

                toast.responseErrors(e)
                return {
                    data: [],
                    total: 0
                }
            }
        } else {
            return {
                data: [],
                total: 0
            }
        }
    }

    const notesColumns: ICustomColumn[] = [
        { field: 'note', title: 'Note', filterType: 'text', cellType: GridCellEnum.Concatenate, },
        { field: 'private', title: 'Private', cellType: GridCellEnum.YesNoBoolean, width: '75px' },
        { field: 'createdByName', title: 'Created By', filterType: 'text', width: '150px' },
        { field: 'createdDate', title: 'Created Date', format: '{0:g}', filterType: 'date', width: '175px' },
        { field: 'lastModifiedByName', title: 'Last Modified By', filterType: 'text', width: '150px' },
        { field: 'lastModifiedDate', title: 'Last Modified Date', format: '{0:g}', filterType: 'date', width: '175px' },
    ]

    const renderRow = (row: React.ReactElement<HTMLTableRowElement>, props: GridRowProps) => {

        const note = props.dataItem as NoteDto
        const canEdit = canEditNote(note)

        const trProps = {
            ...row.props,
            title: canEdit
                ? 'Click here to edit this note'
                : 'You do not have permission to perform this action'
        }

        return React.cloneElement(row, trProps, row.props.children as ReactNode)
    }

    const rowClicked = (e: GridRowClickEvent) => {
        const note = e.dataItem as NoteDto

        if (!canEditNote(note)) {
            toast.error(DEFAULT_PERMISSION_MESSAGE)
            return
        }

        setEditNote(note)
        setShowEditModal(true)
    }

    const onAddNote = async () => {

        if (!canAddNote()) {
            toast.error(DEFAULT_PERMISSION_MESSAGE)
            return
        }

        setShowAddModal(true)
    }

    // checks what item we are adding a note too and if the user has the appropriate permission
    const canAddNote = (): boolean => {

        if (noteType === NoteTypeEnum.Dealer && !hasPermission(DcsPermissions.AddNoteDealer))
            return false
        if (noteType === NoteTypeEnum.Case && !hasPermission(DcsPermissions.AddNoteCase))
            return false
        if (noteType === NoteTypeEnum.Task && !hasPermission(DcsPermissions.AddNoteTask))
            return false
        if (noteType === NoteTypeEnum.Lead && !hasPermission(DcsPermissions.AddNoteLead))
            return false
        if (noteType === NoteTypeEnum.Development && !hasPermission(DcsPermissions.AddNoteDevelopment))
            return false
        if (noteType === NoteTypeEnum.Importer && !hasPermission(DcsPermissions.AddNoteImporter))
            return false
        if (noteType === NoteTypeEnum.Exporter && !hasPermission(DcsPermissions.AddNoteExporter))
            return false
        if (noteType === NoteTypeEnum.DealerContact && !hasPermission(DcsPermissions.AddNoteDealerContact))
            return false

        return true
    }

    // checks what item the note is associated with and if the user has the appropriate permission
    const canEditNote = (note: NoteDto): boolean => {

        if (note.dealerId && !hasPermission(DcsPermissions.EditNoteDealer))
            return false
        if (note.caseId && !hasPermission(DcsPermissions.EditNoteCase))
            return false
        if (note.taskId && !hasPermission(DcsPermissions.EditNoteTask))
            return false
        if (note.prospectId && !hasPermission(DcsPermissions.EditNoteLead))
            return false
        if (note.developmentId && !hasPermission(DcsPermissions.EditNoteDevelopment))
            return false
        if (note.importerId && !hasPermission(DcsPermissions.EditNoteImporter))
            return false
        if (note.exporterSfId && !hasPermission(DcsPermissions.EditNoteExporter))
            return false
        if (note.dealerContactId && !hasPermission(DcsPermissions.EditNoteDealerContact))
            return false

        return true
    }


    const createNote = async (note: CreateNoteDto) => {

        if (noteTypeId) {

            await dispatch(startLoadingAction())
            setDisableSubmit(true)
            try {

                const createNote = new CreateNoteDto({
                    id: noteTypeId,
                    noteType: noteType,
                    note: note.note,
                    private: note.private
                })

                let createdNote = await V1ApiSharedClient.createNote(createNote)

                if (createdNote.payload?.caseId) {
                    await V1ApiCasesClient.sendCaseNoteEmail(true, createdNote.payload)
                }

                toast.success('Note was successfully created.')
                setAddNoteModalErrors([])
                setShowAddModal(false)
                setRefreshGrid(refresh => refresh + 1) // toggle to refresh the grid

            } catch (e) {

                toast.responseErrors(e)
            } finally {

                await dispatch(endLoadingAction())
                setDisableSubmit(false)
            }
        } else {

            toast.error('Note Type Id is undefined.')
        }
    }

    const updateNote = async (note: UpdateNoteDto) => {

        if (editNote) {

            await dispatch(startLoadingAction())
            setDisableSubmit(true)
            try {

                const updateNote = new UpdateNoteDto({
                    noteType: noteType,
                    note: note.note,
                    private: note.private
                })

                await V1ApiSharedClient.updateNote(editNote.sfnoteId, updateNote)

                if (editNote.caseId) {
                    await V1ApiCasesClient.sendCaseNoteEmail(false, editNote)
                }

                toast.success('Note was successfully updated.')
                setEditNoteModalErrors([])
                setShowEditModal(false)
                setRefreshGrid(refresh => refresh + 1) // toggle to refresh the grid
            } catch (e) {

                toast.responseErrors(e)
            } finally {

                await dispatch(endLoadingAction())
                setDisableSubmit(false)
            }
        } else {

            toast.error('Note Type Id is undefined.')
        }
    }

    useEffect(() => {
        if (gridRef.current)
            gridRef.current.fetch()
    }, [noteTypeId, refreshGrid])


    return (
        <div id='NotesForm'>
            <div className='bg-light shadow-sm p-3 border'>
                <Row>
                    <Col md={6}><h4>{noteType} Notes</h4></Col>
                    <Col md={6} className='text-start text-md-end' style={{ alignSelf: 'center' }}>
                        <DefaultButton
                            className='btn-sm'
                            variant='default'
                            onClick={onAddNote}
                        >
                            <FontAwesomeIcon icon={['fas', 'plus-circle']} /> {' '}
                            Add Note
                        </DefaultButton>
                    </Col>
                    {!hideDateOptions ?
                    <Col xs={12}>
                        <Form
                            ref={formRef}
                            ignoreModified={true}
                            render={(formRenderProps) => (
                                <FormElement>
                                    <FormDateSpanSelector
                                        ref={formDateSpanSelectorRef}
                                        className=''
                                        formRenderProps={formRenderProps}
                                        localStorageNames={dateSearchLocalStorageNames}
                                        onSearch={searchByDate}
                                        maxDate={maxDate}
                                    />
                                </FormElement>
                            )}
                        />
                    </Col>
                    : <></>}
                </Row>
                <hr />
                <Row>
                    {gridLocalStorageNames &&
                        <Col xs={12}>
                            <SAGrid
                                ref={gridRef}
                                features={
                                    {
                                        pageable: true,
                                        sortable: true,
                                        reorderColumns: false,
                                        simpleSearch: true
                                    }
                                }
                                columns={notesColumns}
                                defaultSort={[{ field: 'lastModifiedDate', dir: 'desc' }]}
                                getData={getData}
                                onRowClick={rowClicked}
                                renderRow={renderRow}
                                localStorageNames={gridLocalStorageNames}
                            />
                        </Col>
                    }
                </Row>
            </div>

            <ConfirmModal className='modal-lg'
                disabled={disableSubmit}
                loading={disableSubmit}
                errors={addNoteModalErrors}
                show={showAddModal}
                title='Add Note'
                confirmButtonTitle='Add Note'
                onCancel={() => setShowAddModal(false)}
                onSave={(e) => {
                    let keyPress = (e as unknown as KeyboardEvent).key
                    if (keyPress === 'Enter') {
                        return
                    } else {
                        formAddModal.current?.onSubmit(e)
                    }
                }}
                content={
                    <Form
                        ref={formAddModal}
                        ignoreModified={true}
                        onSubmit={(e) => createNote(e as CreateNoteDto)}
                        render={() => (
                            <FormElement>
                                <Row>
                                    <Col md={12}>
                                        <Field
                                            required={true}
                                            validator={RequiredValidator}
                                            component={FormTextArea}
                                            name='note'
                                            label='Note:'
                                            rows={10}
                                        />
                                    </Col>
                                    <Col md={12}>
                                        <Field
                                            component={FormSwitch}
                                            label='Private?'
                                            name='private'
                                            onLabel='YES'
                                            offLabel='NO'
                                        />
                                    </Col>
                                </Row>

                            </FormElement>
                        )}
                    />
                }
            />

            <ConfirmModal className='modal-lg'
                errors={editNoteModalErrors}
                show={showEditModal}
                title='Edit Note'
                confirmButtonTitle='Save'
                onCancel={() => setShowEditModal(false)}
                onSave={(e) => {
                    let keyPress = (e as unknown as KeyboardEvent).key
                    if (keyPress === 'Enter') {
                        return
                    } else {
                        formEditModal.current?.onSubmit(e)
                    }
                }}
                disabled={disableSubmit}
                loading={disableSubmit}
                content={
                    <Form
                        ref={formEditModal}
                        ignoreModified={true}
                        onSubmit={(e) => updateNote(e as NoteDto)}
                        initialValues={editNote}
                        render={() => (
                            <FormElement>
                                {editNote &&
                                    <Row>
                                        <Col md={12}>
                                            <Field
                                                required
                                                validator={RequiredValidator}
                                                component={FormTextArea}
                                                name='note'
                                                label='Note:'
                                                value={editNote?.note}
                                                rows={10}
                                            />
                                        </Col>
                                        <Col md={12}>
                                            <Field
                                                component={FormSwitch}
                                                label='Private?'
                                                name='private'
                                                onLabel='YES'
                                                offLabel='NO'
                                                checked={editNote.private}
                                                onChange={(e) => editNote.private = e.target.value}
                                            />
                                        </Col>
                                    </Row>
                                }
                            </FormElement>
                        )}
                    />
                }
            />

        </div>
    )
}
export default NotesForm